/**
 * missionBrief — deterministic what-was-built projections for fleet runs
 * (2026-08-14, K3-consulted design: docs in scratch k3-mission-panel.md).
 *
 * Doctrine: QUOTE-ONLY. These functions are pure, deterministic TRIMS of
 * recorded text (a view, not a summary) — they never reword. The item brief
 * picks the first imperative sentence of the task spec (skipping the
 * 'READ FIRST: docs/plans/…' preamble) and clamps it with an ellipsis;
 * fallbacks quote the plan-doc filename or state the honest dead end.
 *
 * Failure mode to test for (K3): any brief still starting with 'READ FIRST'
 * is a bug. Version the verb list by explicit edit (EXTRACTOR_VERSION).
 */
import type { FleetRun } from '../../services/fleetHudApi';

export const EXTRACTOR_VERSION = 'v1';

const PREAMBLE = /^\s*(read first|read:|context|background|reference|see also|note)\b/i;
const BULLET = /^\s*(?:[-*•]|\d+[.)])\s*/;
const IMPERATIVE =
  /^(implement|add|build|create|wire|refactor|fix|extend|replace|update|remove|delete|extract|introduce|port|migrate|integrate|hook|render|write|scaffold|split|move|rename|guard|harden|surface|teach|derive|expose|pin|normalize)\b/i;
const SENT_SPLIT = /(?<=[.!?:;])\s+/; // colons included: specs read 'Goal: X. Do Y.'

function clamp(s: string, n: number): string {
  if (s.length <= n) return s;
  const c = s.slice(0, n);
  const cut = c.lastIndexOf(' ');
  return (cut > 0 ? c.slice(0, cut) : c) + ' …';
}

/** One readable line quoted out of a raw task spec. Deterministic; never reworded. */
export function itemBrief(task: string | undefined | null): string {
  if (!task) return '';
  let body = String(task).replace(/\r/g, '').trim();

  // 1. Drop a 'READ FIRST …' style preamble: cut to the first blank line,
  //    else drop the first line.
  if (PREAMBLE.test(body)) {
    const i = body.search(/\n\s*\n/);
    body = i > -1 ? body.slice(i).trim() : body.split('\n').slice(1).join('\n').trim();
  }

  // 2. First sentence opening with an imperative verb wins.
  const sents = body
    .split(SENT_SPLIT)
    .map((s) =>
      s
        .replace(BULLET, '')
        .replace(/[*`#_]/g, '')
        .replace(/\s*\((see|per|ref)\b[^)]*\)\s*$/i, '')
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter(Boolean);

  const pick = sents.find((s) => IMPERATIVE.test(s)) ?? sents.find((s) => s.length >= 25);

  if (pick) {
    const clause = pick.split(/\s+[—–]\s+|;\s+/)[0];
    return clamp(clause.length >= 30 ? clause : pick, 110);
  }

  // Fallback B: quote the plan-doc filename — still recorded data.
  const m = String(task).match(/docs\/plans\/([\w.-]+)\.md/i);
  if (m) return `per plan: ${m[1]}`;

  return `task spec ${String(task).length} chars — see full text`; // honest dead end
}

/** Mission name — the run's phase name(s), verbatim. */
export function missionName(run: Pick<FleetRun, 'phase_configs'>): string {
  const names = (run.phase_configs ?? []).map((p) => p.name).filter(Boolean) as string[];
  return names.join(' · ');
}

/** First clause of the operator-authored description: cut at ' — ' or 90 chars. */
export function missionSummary(run: Pick<FleetRun, 'phase_configs'>): string {
  const desc = (run.phase_configs ?? []).map((p) => p.description).find(Boolean) ?? '';
  const clause = desc.split(/\s+—\s+/)[0];
  return clamp(clause, 90);
}

/** Full description, verbatim (expanded surfaces). */
export function missionDescription(run: Pick<FleetRun, 'phase_configs'>): string {
  return (run.phase_configs ?? []).map((p) => p.description).find(Boolean) ?? '';
}

/**
 * item_N (1-based within the run) → its raw task spec, mapped across the
 * phases' task lists in order. Missing → undefined (render omits the brief —
 * honest omission, never a guess).
 */
export function taskForItem(
  run: Pick<FleetRun, 'phase_configs'>,
  itemId: string | undefined
): string | undefined {
  const m = String(itemId ?? '').match(/(\d+)\s*$/);
  if (!m) return undefined;
  const idx = parseInt(m[1], 10) - 1;
  if (idx < 0) return undefined;
  const flat = (run.phase_configs ?? []).flatMap((p) => p.tasks ?? []);
  return flat[idx];
}

/** Outcome tally glyph string from items_summary — real statuses only. */
export function outcomeTally(run: Pick<FleetRun, 'items_summary'>): string {
  const items = run.items_summary ?? [];
  if (items.length === 0) return '';
  let merged = 0;
  let dead = 0;
  let held = 0;
  for (const it of items) {
    const s = String(it.status ?? '');
    if (s === 'merged' || s === 'completed') merged += 1;
    else if (s === 'rolled_back' || s.startsWith('failed')) dead += 1;
    else held += 1;
  }
  const parts: string[] = [];
  if (merged) parts.push(`▣${merged}`);
  if (held) parts.push(`▢${held}`);
  if (dead) parts.push(`▨${dead}`);
  return parts.join(' ');
}
